/**
 * LEITOR XLSX (Bloco 04) — porta única de leitura de planilha do sistema.
 *
 * Um .xlsx é um ZIP de XMLs. Aqui ele é aberto sem nenhuma dependência
 * externa e sem rede, usando apenas APIs presentes tanto no navegador quanto
 * no Node (DecompressionStream "deflate-raw"). Nada além desta camada conhece
 * ZIP ou XML: o resto do sistema recebe apenas `SpreadsheetTable`.
 *
 * Três armadilhas reais da massa oficial de teste, tratadas aqui:
 * 1. O arquivo usa tags COM prefixo de namespace (`<x:row>`, `<x:c>`). Um
 *    parser que só aceita `<row>` lê zero linhas.
 * 2. As abas têm linhas vazias de sobra depois dos dados (2100 elementos de
 *    linha para 2000 registros), então o fim dos dados vem da tabela nomeada
 *    (`<table ref="A5:V2005">`) quando existe, não da contagem de linhas.
 * 3. Há um banner de título antes do cabeçalho, então a linha de cabeçalho é
 *    detectada, nunca assumida como a primeira.
 */

import type { SpreadsheetRow, SpreadsheetTable, SpreadsheetWorkbook } from "../../domain/import/spreadsheet.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";

export type { SpreadsheetRow, SpreadsheetTable, SpreadsheetWorkbook };

/**
 * Normaliza qualquer entrada binária recebida (ArrayBuffer, Uint8Array, Buffer,
 * Array numérico ou Objeto indexado do transporte JSON/RPC) para um Uint8Array
 * contíguo com ArrayBuffer válido.
 */
export function normalizeWorkbookBytes(input: unknown): Uint8Array {
  if (input === null || input === undefined) {
    throw new AppError(ErrorCode.Validation, "Arquivo de planilha ausente ou inválido.", {
      safeMessage: "Arquivo de planilha ausente ou inválido."
    });
  }

  // 1. ArrayBuffer
  if (input instanceof ArrayBuffer) {
    return new Uint8Array(input);
  }

  // 2. TypedArray / Uint8Array / Node Buffer
  if (ArrayBuffer.isView(input)) {
    const view = input as ArrayBufferView;
    const buffer = view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength);
    return new Uint8Array(buffer);
  }

  // 3. Array numérico (deserializado de JSON [80, 75, ...])
  if (Array.isArray(input)) {
    if (input.some(v => typeof v !== "number" || v < 0 || v > 255 || !Number.isInteger(v))) {
      throw new AppError(ErrorCode.Validation, "Bytes da planilha em formato numérico inválido.", {
        safeMessage: "Estrutura do arquivo de planilha corrompida ou inválida."
      });
    }
    return Uint8Array.from(input);
  }

  // 4. Objeto indexado de bytes vindo da deserialização JSON.stringify(Uint8Array) -> { "0": 80, "1": 75, ... }
  if (typeof input === "object" && input !== null) {
    const keys = Object.keys(input);
    if (keys.length > 0 && keys.every(k => /^\d+$/.test(k))) {
      const len = keys.length;
      const arr = new Uint8Array(len);
      const record = input as Record<string, unknown>;
      for (let i = 0; i < len; i++) {
        const val = record[String(i)];
        if (typeof val !== "number" || val < 0 || val > 255 || !Number.isInteger(val)) {
          throw new AppError(ErrorCode.Validation, "Bytes da planilha em formato indexado inválido.", {
            safeMessage: "Estrutura do arquivo de planilha corrompida ou inválida."
          });
        }
        arr[i] = val;
      }
      return arr;
    }
  }

  // 5. Contrato binário RPC canônico { __rpcType: "binary", encoding: "base64", data: "..." }
  if (
    typeof input === "object" &&
    input !== null &&
    (input as any).__rpcType === "binary" &&
    (input as any).encoding === "base64" &&
    typeof (input as any).data === "string"
  ) {
    const clean = (input as any).data.trim();
    if (typeof Buffer !== "undefined") {
      const buf = Buffer.from(clean, "base64");
      return new Uint8Array(buf.buffer.slice(buf.byteOffset, buf.byteOffset + buf.byteLength));
    } else if (typeof atob !== "undefined") {
      const binaryString = atob(clean);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes;
    }
  }

  // 6. String base64 ou Data URL
  if (typeof input === "string") {
    const clean = (input.startsWith("data:") ? input.split(",")[1] || "" : input).trim();
    if (clean.length === 0 || clean.length % 4 !== 0 || !/^[A-Za-z0-9+/]+={0,2}$/.test(clean)) {
      throw new AppError(ErrorCode.Validation, "Formato do arquivo de planilha inválido.", {
        safeMessage: "Formato do arquivo de planilha inválido. Envie um arquivo .xlsx válido."
      });
    }
    try {
      if (typeof Buffer !== "undefined") {
        const buf = Buffer.from(clean, "base64");
        return new Uint8Array(buf.buffer.slice(buf.byteOffset, buf.byteOffset + buf.byteLength));
      } else if (typeof atob !== "undefined") {
        const binaryString = atob(clean);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
      }
    } catch {
      throw new AppError(ErrorCode.Validation, "Formato do arquivo de planilha inválido.", {
        safeMessage: "Formato do arquivo de planilha inválido."
      });
    }
  }

  throw new AppError(ErrorCode.Validation, "Formato do arquivo de planilha inválido.", {
    safeMessage: "Formato do arquivo de planilha não suportado. Envie um arquivo .xlsx válido."
  });
}

const NS = "(?:[A-Za-z0-9]+:)?";

// ------------------------------------------------------------------ ZIP

interface ZipEntry {
  readonly method: number;
  readonly compressedSize: number;
  readonly localOffset: number;
}

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  const buffer = await new Response(stream).arrayBuffer();
  return new Uint8Array(buffer);
}

class ZipArchive {
  private constructor(
    private readonly bytes: Uint8Array,
    private readonly view: DataView,
    private readonly entries: Map<string, ZipEntry>
  ) {}

  static open(bytes: Uint8Array): ZipArchive {
    // Ensure bytes.buffer is a valid ArrayBuffer.
    // If the buffer is missing/invalid (e.g., from an HTTP upload or detached ArrayBuffer),
    // reconstruct it from the Uint8Array.
    let buffer = bytes.buffer;
    let byteOffset = bytes.byteOffset;
    let byteLength = bytes.byteLength;

    if (!buffer || buffer.byteLength === 0) {
      const temp = new Uint8Array(bytes);
      buffer = temp.buffer;
      byteOffset = 0;
      byteLength = temp.length;
      bytes = temp;
    }

    const view = new DataView(buffer, byteOffset, byteLength);
    let eocd = -1;
    const floor = Math.max(0, bytes.length - 70000);
    for (let i = bytes.length - 22; i >= floor; i--) {
      if (view.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
    }
    if (eocd < 0) {
      throw new SpreadsheetFormatError("O arquivo não é uma planilha .xlsx válida (assinatura ZIP não encontrada).");
    }
    const total = view.getUint16(eocd + 10, true);
    let offset = view.getUint32(eocd + 16, true);
    const entries = new Map<string, ZipEntry>();
    const decoder = new TextDecoder("utf-8");
    for (let n = 0; n < total; n++) {
      if (view.getUint32(offset, true) !== 0x02014b50) {
        throw new SpreadsheetFormatError("Índice interno do arquivo .xlsx está corrompido.");
      }
      const method = view.getUint16(offset + 10, true);
      const compressedSize = view.getUint32(offset + 20, true);
      const nameLength = view.getUint16(offset + 28, true);
      const extraLength = view.getUint16(offset + 30, true);
      const commentLength = view.getUint16(offset + 32, true);
      const localOffset = view.getUint32(offset + 42, true);
      const name = decoder.decode(bytes.subarray(offset + 46, offset + 46 + nameLength));
      entries.set(name, { method, compressedSize, localOffset });
      offset += 46 + nameLength + extraLength + commentLength;
    }
    return new ZipArchive(bytes, view, entries);
  }

  has(name: string): boolean {
    return this.entries.has(name);
  }

  names(): string[] {
    return [...this.entries.keys()];
  }

  async text(name: string): Promise<string | null> {
    const entry = this.entries.get(name);
    if (!entry) return null;
    if (this.view.getUint32(entry.localOffset, true) !== 0x04034b50) {
      throw new SpreadsheetFormatError(`Entrada interna inválida no .xlsx: ${name}`);
    }
    const nameLength = this.view.getUint16(entry.localOffset + 26, true);
    const extraLength = this.view.getUint16(entry.localOffset + 28, true);
    const start = entry.localOffset + 30 + nameLength + extraLength;
    const raw = this.bytes.subarray(start, start + entry.compressedSize);
    let content: Uint8Array;
    if (entry.method === 0) content = raw;
    else if (entry.method === 8) content = await inflateRaw(raw);
    else throw new SpreadsheetFormatError(`Compressão não suportada no .xlsx (método ${entry.method}).`);
    return new TextDecoder("utf-8").decode(content);
  }
}

/** Erro de formato do arquivo — distinto de erro de conteúdo/validação de negócio. */
export class SpreadsheetFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SpreadsheetFormatError";
  }
}

// ------------------------------------------------------------------ XML

function decodeXmlEntities(value: string): string {
  return value
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#x([0-9a-fA-F]+);/g, (_, hex: string) => String.fromCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec: string) => String.fromCodePoint(parseInt(dec, 10)))
    .replace(/&amp;/g, "&");
}

function attr(tag: string, name: string): string | null {
  const match = new RegExp(`${name}="([^"]*)"`).exec(tag);
  return match ? (match[1] ?? null) : null;
}

/** "B7" -> 1 (índice de coluna 0-based). */
export function columnIndexOf(cellRef: string): number {
  const letters = (/^([A-Z]+)/.exec(cellRef) || [])[1] || "A";
  let index = 0;
  for (const ch of letters) index = index * 26 + (ch.charCodeAt(0) - 64);
  return index - 1;
}

function rowNumberOf(cellRef: string): number {
  const digits = (/(\d+)$/.exec(cellRef) || [])[1];
  return digits ? parseInt(digits, 10) : 0;
}

// ------------------------------------------------------------- planilha

interface RawSheet {
  readonly name: string;
  /** Linhas por número de linha da planilha (1-based) → células por índice de coluna. */
  readonly rows: Map<number, Map<number, string>>;
  /** Última linha de dados declarada pela tabela nomeada, quando existe. */
  readonly tableLastRow: number | null;
  readonly tableHeaderRow: number | null;
}

function parseSharedStrings(xml: string | null): string[] {
  if (!xml) return [];
  const out: string[] = [];
  const siRe = new RegExp(`<${NS}si\\b[^>]*>([\\s\\S]*?)<\\/${NS}si>|<${NS}si\\b[^>]*\\/>`, "g");
  const tRe = new RegExp(`<${NS}t\\b[^>]*>([\\s\\S]*?)<\\/${NS}t>`, "g");
  let match: RegExpExecArray | null;
  while ((match = siRe.exec(xml)) !== null) {
    const inner = match[1] ?? "";
    let text = "";
    tRe.lastIndex = 0;
    let run: RegExpExecArray | null;
    while ((run = tRe.exec(inner)) !== null) text += run[1] ?? "";
    out.push(decodeXmlEntities(text));
  }
  return out;
}

function parseSheetXml(name: string, xml: string, shared: readonly string[], table: { headerRow: number; lastRow: number } | null): RawSheet {
  const rows = new Map<number, Map<number, string>>();
  const rowRe = new RegExp(`<${NS}row\\b([^>]*)>([\\s\\S]*?)<\\/${NS}row>|<${NS}row\\b([^>]*)\\/>`, "g");
  const cellRe = new RegExp(`<${NS}c\\b([^>]*?)(?:\\/>|>([\\s\\S]*?)<\\/${NS}c>)`, "g");
  const tRe = new RegExp(`<${NS}t\\b[^>]*>([\\s\\S]*?)<\\/${NS}t>`, "g");
  const vRe = new RegExp(`<${NS}v\\b[^>]*>([\\s\\S]*?)<\\/${NS}v>`);

  let sequential = 0;
  let rowMatch: RegExpExecArray | null;
  while ((rowMatch = rowRe.exec(xml)) !== null) {
    sequential++;
    const rowAttrs = rowMatch[1] ?? rowMatch[3] ?? "";
    const declared = attr(rowAttrs, "r");
    const rowNumber = declared ? parseInt(declared, 10) : sequential;
    const inner = rowMatch[2] ?? "";
    if (!inner) continue;

    const cells = new Map<number, string>();
    cellRe.lastIndex = 0;
    let cellMatch: RegExpExecArray | null;
    let fallbackColumn = 0;
    while ((cellMatch = cellRe.exec(inner)) !== null) {
      const cellAttrs = cellMatch[1] ?? "";
      const body = cellMatch[2] ?? "";
      const ref = attr(cellAttrs, "r");
      const type = attr(cellAttrs, "t") ?? "";
      const columnIndex = ref ? columnIndexOf(ref) : fallbackColumn;
      fallbackColumn = columnIndex + 1;

      let value = "";
      if (type === "inlineStr") {
        tRe.lastIndex = 0;
        let run: RegExpExecArray | null;
        while ((run = tRe.exec(body)) !== null) value += run[1] ?? "";
        value = decodeXmlEntities(value);
      } else {
        const rawValue = (vRe.exec(body) || [])[1];
        if (rawValue !== undefined) {
          if (type === "s") {
            value = shared[parseInt(rawValue, 10)] ?? "";
          } else {
            value = decodeXmlEntities(rawValue);
          }
        }
      }
      if (value !== "") cells.set(columnIndex, value);
    }
    if (cells.size > 0) rows.set(rowNumber, cells);
  }

  return {
    name,
    rows,
    tableHeaderRow: table ? table.headerRow : null,
    tableLastRow: table ? table.lastRow : null
  };
}

/**
 * Descobre a linha de cabeçalho quando não há tabela nomeada: a primeira
 * linha com pelo menos 2 células preenchidas e que seja seguida por outra
 * linha com pelo menos a mesma largura. Isso descarta o banner de título,
 * que tem uma única célula.
 */
function detectHeaderRow(sheet: RawSheet): number | null {
  if (sheet.tableHeaderRow !== null) return sheet.tableHeaderRow;
  const numbers = [...sheet.rows.keys()].sort((a, b) => a - b);
  for (let i = 0; i < numbers.length; i++) {
    const current = sheet.rows.get(numbers[i]!)!;
    if (current.size < 2) continue;
    const next = i + 1 < numbers.length ? sheet.rows.get(numbers[i + 1]!) : undefined;
    if (next && next.size >= 2) return numbers[i]!;
  }
  return numbers.length > 0 ? (numbers[0] ?? null) : null;
}

function buildTable(sheet: RawSheet): SpreadsheetTable | null {
  const headerRowNumber = detectHeaderRow(sheet);
  if (headerRowNumber === null) {
    return { sheetName: sheet.name, headers: [], headerRowNumber: 0, rows: [] };
  }
  const headerCells = sheet.rows.get(headerRowNumber) ?? new Map<number, string>();
  const maxColumn = Math.max(-1, ...[...headerCells.keys()]);
  const headers: string[] = [];
  for (let c = 0; c <= maxColumn; c++) headers.push((headerCells.get(c) ?? "").trim());

  const lastRow = sheet.tableLastRow;
  const rows: SpreadsheetRow[] = [];
  const numbers = [...sheet.rows.keys()].filter(n => n > headerRowNumber).sort((a, b) => a - b);
  for (const rowNumber of numbers) {
    if (lastRow !== null && rowNumber > lastRow) break;
    const cells = sheet.rows.get(rowNumber)!;
    const values: Record<string, string> = {};
    let filled = 0;
    headers.forEach((header, columnIndex) => {
      if (!header) return;
      const value = (cells.get(columnIndex) ?? "").trim();
      values[header] = value;
      if (value !== "") filled++;
    });
    // Linha totalmente vazia nas colunas do cabeçalho é cauda de preenchimento, não registro.
    if (filled === 0) continue;
    rows.push({ rowNumber, values });
  }

  return { sheetName: sheet.name, headers, headerRowNumber, rows };
}

async function loadTableRanges(zip: ZipArchive, sheetPath: string): Promise<{ headerRow: number; lastRow: number } | null> {
  const relsPath = sheetPath.replace(/^(.*)\/([^/]+)$/, "$1/_rels/$2.rels");
  const relsXml = await zip.text(relsPath);
  if (!relsXml) return null;
  const targets: string[] = [];
  const relRe = /<Relationship\b[^>]*?\/?>/g;
  let match: RegExpExecArray | null;
  while ((match = relRe.exec(relsXml)) !== null) {
    const target = attr(match[0], "Target");
    const type = attr(match[0], "Type") ?? "";
    if (target && type.endsWith("/table")) targets.push(target);
  }
  let best: { headerRow: number; lastRow: number } | null = null;
  for (const rawTarget of targets) {
    let path = rawTarget.startsWith("/") ? rawTarget.slice(1) : rawTarget;
    if (path.startsWith("../")) path = "xl/" + path.slice(3);
    else if (!path.startsWith("xl/")) path = "xl/tables/" + path.replace(/^.*\//, "");
    const tableXml = await zip.text(path);
    if (!tableXml) continue;
    const ref = attr(tableXml, "ref");
    if (!ref) continue;
    const [from, to] = ref.split(":");
    if (!from || !to) continue;
    const headerRow = rowNumberOf(from);
    const lastRow = rowNumberOf(to);
    if (!headerRow || !lastRow) continue;
    if (!best || lastRow - headerRow > best.lastRow - best.headerRow) best = { headerRow, lastRow };
  }
  return best;
}

/** Abre um .xlsx a partir dos bytes do arquivo. Não faz rede e não usa dependência externa. */
export async function readWorkbook(rawBytes: unknown): Promise<SpreadsheetWorkbook> {
  const bytes = normalizeWorkbookBytes(rawBytes);
  const zip = ZipArchive.open(bytes);
  const workbookXml = await zip.text("xl/workbook.xml");
  if (!workbookXml) throw new SpreadsheetFormatError("O arquivo não é uma planilha .xlsx válida (workbook ausente).");

  const relsXml = (await zip.text("xl/_rels/workbook.xml.rels")) ?? "";
  const rels = new Map<string, string>();
  const relRe = /<Relationship\b[^>]*?\/?>/g;
  let relMatch: RegExpExecArray | null;
  while ((relMatch = relRe.exec(relsXml)) !== null) {
    const id = attr(relMatch[0], "Id");
    const target = attr(relMatch[0], "Target");
    if (id && target) rels.set(id, target);
  }

  const shared = parseSharedStrings(await zip.text("xl/sharedStrings.xml"));

  const declarations: Array<{ name: string; path: string }> = [];
  const sheetRe = new RegExp(`<${NS}sheet\\b[^>]*\\/?>`, "g");
  let sheetMatch: RegExpExecArray | null;
  while ((sheetMatch = sheetRe.exec(workbookXml)) !== null) {
    const tag = sheetMatch[0];
    const name = decodeXmlEntities(attr(tag, "name") ?? "");
    const rid = attr(tag, "r:id") ?? attr(tag, "id") ?? "";
    let target = rels.get(rid) ?? "";
    if (!target) continue;
    if (target.startsWith("/")) target = target.slice(1);
    else if (!target.startsWith("xl/")) target = "xl/" + target;
    declarations.push({ name, path: target });
  }

  const tables = new Map<string, SpreadsheetTable>();
  for (const declaration of declarations) {
    const xml = await zip.text(declaration.path);
    if (xml === null) continue;
    const range = await loadTableRanges(zip, declaration.path);
    const raw = parseSheetXml(declaration.name, xml, shared, range);
    const table = buildTable(raw);
    if (table) tables.set(declaration.name, table);
  }

  return {
    sheetNames: declarations.map(d => d.name),
    table: (sheetName: string) => tables.get(sheetName) ?? null
  };
}

export { findSheetName } from "../../domain/import/spreadsheet.js";
